import React, { useState } from 'react';
import classNames from 'classnames';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';

import { Tooltip } from 'core/components/tooltip';
import { Label } from 'core/components/label';
import { ActionButton } from 'core/components/action-button';
import { currencyFormatter } from 'core/helpers/formatters';
import { tooltipHoverTexts } from 'core/helpers/constants';

import closeIcon from 'core/assets/svg/x.svg';
import arrowIcon from 'core/assets/svg/ic-arrow.svg';
import caret from 'core/assets/svg/caretOrange.svg';
import useStyles from './compare-renewal-price-factors-modal.styles';

// These never explain an increase: property_age is always present for home, and rate_cap limits an increase.
const NON_EXPLANATORY_FACTOR_KEYS = ['property_age', 'rate_cap'];

/** Rounded before formatting so float noise never renders as a signed $0.00. */
const delta = (current, prior) => Math.round(((current || 0) - (prior || 0)) * 100) / 100;

const signedCurrency = (amount) => `${amount > 0 ? '+' : amount < 0 ? '-' : ''}${currencyFormatter(Math.abs(amount))}`;

const discountSummary = (discountChanges) => {
  const removed = discountChanges.filter((discount) => discount.status === 'REMOVED').length;
  const added = discountChanges.length - removed;
  return [removed && `${removed} removed`, added && `${added} added`].filter(Boolean).join(', ');
};

const CollapsibleSection = ({ title, summary, defaultOpen = false, children }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={classes.section}>
      <Grid className={classes.sectionHeader}>
        <Label className={classes.sectionTitle}>{title}</Label>
        <div className={classes.sectionTotalGroup}>
          {summary}
          <Button onClick={() => setIsOpen(!isOpen)} variant="text" color="secondary">
            <img className={isOpen ? classes.icon : classes.iconFold} src={caret} alt={isOpen ? 'fold' : 'unfold'} />
          </Button>
        </div>
      </Grid>
      {isOpen && children}
    </div>
  );
};

const CompareRenewalPriceFactorsModal = ({ open, onClose, comparison, loading, error }) => {
  const classes = useStyles();

  const renderBody = () => {
    if (loading) {
      return <div className={classes.loadingState}>Loading price comparison...</div>;
    }
    if (error || !comparison) {
      return <div className={classes.errorState}>We couldn&apos;t load the price comparison for this policy.</div>;
    }

    const { priorTerm, normalizedPriorTerm, currentTerm, discountChanges = [], relevantFactors = [] } = comparison;

    const premiumChange = delta(currentTerm.premium, priorTerm.premium);
    const surplusChange = delta(currentTerm.surplus, priorTerm.surplus);
    const feesChange = delta(currentTerm.fees, priorTerm.fees);
    const totalPriceChange = delta(currentTerm.totalPrice, priorTerm.totalPrice);
    // The header compares against the normalized term when there is one, so a mid-term change
    // charged for part of the term does not read as an increase. Totals stays on what was billed.
    const headlineChange = delta(currentTerm.totalPrice, (normalizedPriorTerm || priorTerm).totalPrice);

    // Brands that don't charge the liability surplus contribution must not see it mentioned (#33827).
    const showSurplus = priorTerm.surplus > 0 || currentTerm.surplus > 0;

    const headline =
      headlineChange === 0
        ? { color: classes.grey, text: 'No change' }
        : {
            color: headlineChange > 0 ? classes.red : classes.green,
            text: `${currencyFormatter(Math.abs(headlineChange))} ${headlineChange > 0 ? 'increase' : 'decrease'}`
          };

    // Itemized factors only explain premium; the header guard keeps the note from contradicting a total decrease.
    const showUnexplainedIncreaseNote =
      premiumChange > 0 &&
      headlineChange > 0 &&
      !discountChanges.some((discount) => discount.status === 'REMOVED') &&
      relevantFactors.every((factor) => NON_EXPLANATORY_FACTOR_KEYS.includes(factor.key));

    return (
      <>
        <Grid container className={classes.comparisonRow}>
          <Grid item className={classes.termInfo}>
            <Label className={classes.termLabel}>Prior term</Label>
            <div className={classes.priceColumns}>
              <div className={classes.priceColumn}>
                <div className={classes.priceLabel}>
                  Total price
                  <Tooltip
                    label="More info"
                    text="Total price includes fees and surplus contributions."
                    placement="top"
                    height={14}
                  />
                </div>
                <div className={normalizedPriorTerm ? classes.priceSuperseded : classes.price}>
                  {currencyFormatter(priorTerm.totalPrice)}
                </div>
              </div>
              {normalizedPriorTerm && (
                <div className={classes.priceColumn}>
                  <div className={classes.priceLabel}>
                    Normalized
                    <Tooltip
                      text="The normalized price is calculated as if mid-term changes had been applied to the entire term."
                      placement="top"
                      height={14}
                    />
                  </div>
                  <div className={classes.price}>{currencyFormatter(normalizedPriorTerm.totalPrice)}</div>
                </div>
              )}
            </div>
          </Grid>
          <Grid item className={classes.centerIcon}>
            <img className={classes.arrowIcon} src={arrowIcon} alt="Arrow Icon" />
          </Grid>
          <Grid item className={classes.termInfo}>
            <Label className={classes.termLabel}>Current term</Label>
            <div className={classes.priceRow}>
              <div>
                <div className={classes.priceLabel}>Total price</div>
                <div className={classes.price}>{currencyFormatter(currentTerm.totalPrice)}</div>
              </div>
              <div className={classNames(classes.alert, headline.color)}>{headline.text}</div>
            </div>
          </Grid>
        </Grid>

        {showUnexplainedIncreaseNote && (
          <div className={classes.unexplainedIncreaseNote}>
            No itemized changes explain this increase. It&apos;s most likely from a rate change or rating inputs we
            don&apos;t list here.
          </div>
        )}

        {!!relevantFactors.length && (
          <CollapsibleSection
            title="Relevant factors"
            defaultOpen
            summary={
              <span className={classes.sectionSummary}>
                {relevantFactors.length} {relevantFactors.length === 1 ? 'factor' : 'factors'}
              </span>
            }
          >
            {relevantFactors.map((factor) => (
              <div key={factor.key} className={classes.row}>
                <div className={classes.rowLabel}>{factor.label}</div>
                {factor.detail && <span className={classes.rowDetail}>{factor.detail}</span>}
              </div>
            ))}
          </CollapsibleSection>
        )}

        {!!discountChanges.length && (
          <CollapsibleSection
            title="Discounts"
            summary={<span className={classes.sectionSummary}>{discountSummary(discountChanges)}</span>}
          >
            {discountChanges.map((discount) => (
              <div key={discount.key} className={classes.row}>
                <div className={classes.rowLabel}>{discount.label}</div>
                <div
                  className={classNames(
                    classes.discountStatus,
                    discount.status === 'REMOVED' ? classes.red : classes.green
                  )}
                >
                  {discount.status === 'REMOVED' ? 'Removed' : 'Added'}
                </div>
              </div>
            ))}
          </CollapsibleSection>
        )}

        <div className={classes.section}>
          <Grid className={classes.sectionHeader}>
            <Label className={classes.sectionTitle}>Totals</Label>
          </Grid>
          <div className={classes.totalsRow}>
            <span className={classes.totalsLabel}>Total Premium change</span>
            <span className={classes.rowAmount}>{signedCurrency(premiumChange)}</span>
          </div>
          {showSurplus && (
            <div className={classes.totalsRow}>
              <span className={classes.totalsLabel}>
                Total Surplus change
                <Tooltip className={classes.tooltip} text={tooltipHoverTexts.priceDetailsSurplus} />
              </span>
              <span className={classes.rowAmount}>{signedCurrency(surplusChange)}</span>
            </div>
          )}
          <div className={classes.totalsRow}>
            <span className={classes.totalsLabel}>
              Total Fees change
              <Tooltip className={classes.tooltip} text={tooltipHoverTexts.priceDetailsFees} />
            </span>
            <span className={classes.rowAmount}>{signedCurrency(feesChange)}</span>
          </div>
          <div className={classes.totalsGrandRow}>
            <span className={classes.totalsGrandLabel}>Total price change</span>
            <span className={classes.rowAmount}>{signedCurrency(totalPriceChange)}</span>
          </div>
        </div>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.container }}>
      <ActionButton type="edit" size="big" icon={closeIcon} className={classes.closeButton} onClick={onClose} />
      <DialogTitle>
        <Label className={classes.title}>Total term price comparison</Label>
      </DialogTitle>
      <DialogContent>{renderBody()}</DialogContent>
    </Dialog>
  );
};

export default CompareRenewalPriceFactorsModal;
